import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Hls from 'hls.js';
import { useMutation } from '@apollo/client';
import ProgressMutation from '../../../graphql/mutations/updateProgress';

const getFiniteDuration = (value) => {
  const duration = Number(value);
  return Number.isFinite(duration) && duration > 0 ? duration : 0;
};

const getFiniteTime = (value) => {
  const time = Number(value);
  return Number.isFinite(time) && time >= 0 ? time : 0;
};

const calculatePercent = (value, duration) => {
  const safeDuration = getFiniteDuration(duration);
  const safeValue = getFiniteTime(value);
  if (!safeDuration) return 0;
  return Math.min(100, Math.max(0, (safeValue / safeDuration) * 100));
};

const normalizeWatchedRanges = (ranges) => {
  if (!Array.isArray(ranges)) return [];

  return ranges
    .filter((range) => Array.isArray(range) && range.length >= 2)
    .map(([start, end]) => [Math.max(0, Math.floor(Number(start))), Math.floor(Number(end))])
    .filter(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && end >= start);
};

const expandWatchedRanges = (ranges) => {
  const seconds = [];
  normalizeWatchedRanges(ranges).forEach(([start, end]) => {
    for (let second = start; second <= end; second += 1) {
      seconds.push(second);
    }
  });
  return seconds;
};

const CloudflareVideoPlayer = ({ videoUrl, videoLength, progressId, initialWatchedRanges = [], lastWatched = 0 }) => {
  const { t } = useTranslation();
  // console.log(
  //   `🚀 ~ CloudflareVideoPlayer ~ videoUrl: ${videoUrl}, videoLength: ${videoLength}, progressId: ${progressId}, initialWatchedRanges: ${JSON.stringify(
  //     initialWatchedRanges
  //   )}, lastWatched: ${lastWatched}`
  // );

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const safeInitialWatchedRanges = useMemo(
    () => normalizeWatchedRanges(initialWatchedRanges),
    [initialWatchedRanges]
  );

  // กู้คืนข้อมูลจาก initialWatchedRanges
  const [watchedSeconds, setWatchedSeconds] = useState(() => expandWatchedRanges(initialWatchedRanges));

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => getFiniteTime(lastWatched));
  const [actualVideoLength, setActualVideoLength] = useState(() => getFiniteDuration(videoLength));
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    const restoredSeconds = expandWatchedRanges(safeInitialWatchedRanges);
    setWatchedSeconds((previousSeconds) => {
      const mergedSeconds = Array.from(new Set([...previousSeconds, ...restoredSeconds])).sort((a, b) => a - b);
      const isUnchanged =
        mergedSeconds.length === previousSeconds.length &&
        mergedSeconds.every((second, index) => second === previousSeconds[index]);
      return isUnchanged ? previousSeconds : mergedSeconds;
    });

    const restoredTime = getFiniteTime(lastWatched);
    if (restoredTime > 0) {
      setCurrentTime((previousTime) => Math.max(previousTime, restoredTime));
    }
  }, [lastWatched, safeInitialWatchedRanges]);

  // อัพเดทความยาววิดีโอถ้า prop มีการเปลี่ยนแปลง หรือถ้าไม่มีให้ใช้เมื่อวิดีโอโหลดเสร็จ
  useEffect(() => {
    const duration = getFiniteDuration(videoLength);
    if (duration) {
      setActualVideoLength(duration);
    }
  }, [videoLength]);

  const [updateProgress] = useMutation(ProgressMutation);

  // ฟังก์ชันสำหรับแสดง controls และตั้งเวลาซ่อน
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);

    // ล้าง timeout เก่า
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    // ตั้ง timeout ใหม่ให้ซ่อน controls หลัง 3 วินาที
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        // ซ่อนเฉพาะตอนเล่นอยู่
        setShowControls(false);
      }
    }, 3000);
  }, [isPlaying]);

  // โหลดวิดีโอและตั้งค่าเวลาเริ่มต้น
  useEffect(() => {
    if (!videoRef.current || !videoUrl) return;

    const video = videoRef.current;
    const isHls = videoUrl.includes('.m3u8') || videoUrl.includes('videodelivery.net');
    const hlsUrl = videoUrl.includes('videodelivery.net') && !videoUrl.includes('.m3u8')
      ? `https://videodelivery.net/${videoUrl.match(/videodelivery\.net\/([^/]+)/)?.[1] || videoUrl}/manifest/video.m3u8`
      : videoUrl;

    const updateMediaDuration = () => {
      const duration = getFiniteDuration(video.duration);
      if (duration) setActualVideoLength(duration);
    };

    const restoreLastWatched = () => {
      const watchedAt = getFiniteTime(lastWatched);
      if (watchedAt > 0) video.currentTime = watchedAt;
    };

    video.addEventListener('loadedmetadata', updateMediaDuration);
    video.addEventListener('durationchange', updateMediaDuration);

    if (isHls && Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        restoreLastWatched();
        updateMediaDuration();
      });

      return () => {
        video.removeEventListener('loadedmetadata', updateMediaDuration);
        video.removeEventListener('durationchange', updateMediaDuration);
        hls.destroy();
      };
    } else {
      // สำหรับไฟล์ทั่วไป (เช่น .mp4) หรือ Safari ที่รองรับ HLS ในตัว
      video.src = hlsUrl;
      video.addEventListener('loadedmetadata', restoreLastWatched);

      return () => {
        video.removeEventListener('loadedmetadata', updateMediaDuration);
        video.removeEventListener('durationchange', updateMediaDuration);
        video.removeEventListener('loadedmetadata', restoreLastWatched);
      };
    }
  }, [videoUrl, lastWatched]);

  // เพิ่ม mouse event listeners
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const handleMouseMove = () => {
      showControlsTemporarily();
    };

    const handleMouseLeave = () => {
      if (isPlaying) {
        setShowControls(false);
      }
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isPlaying, showControlsTemporarily]);

  // แสดง controls เมื่อ pause หรือ play
  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    } else {
      showControlsTemporarily();
    }
  }, [isPlaying, showControlsTemporarily]);

  // ล้าง timeout เมื่อ component unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // สร้าง watchedRanges
  function getWatchedRanges(seconds) {
    const sorted = Array.from(new Set(seconds))
      .map((second) => Math.floor(Number(second)))
      .filter((second) => Number.isFinite(second) && second >= 0)
      .sort((a, b) => a - b);
    const ranges = [];
    if (sorted.length === 0) return [];

    let start = sorted[0];
    let end = sorted[0];

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === end + 1) {
        end = sorted[i];
      } else {
        ranges.push([start, end]);
        start = sorted[i];
        end = sorted[i];
      }
    }
    ranges.push([start, end]);
    return ranges;
  }

  // รวม ranges ที่ซ้อนทับกัน
  const mergeRanges = (ranges) => {
    // clone ก่อน sort/merge เพื่อไม่แก้ไข array ต้นทางที่อาจเป็น readonly
    const sorted = ranges
      .filter((range) => Array.isArray(range) && range.length >= 2)
      .map(([start, end]) => [Math.max(0, Math.floor(Number(start))), Math.floor(Number(end))])
      .filter(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && end >= start)
      .sort((a, b) => a[0] - b[0]);
    if (sorted.length === 0) return [];
    const merged = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];
      const last = merged[merged.length - 1];

      // ถ้าซ้อนทับหรือติดกัน ให้รวมกัน
      if (current[0] <= last[1] + 1) {
        last[1] = Math.max(last[1], current[1]);
      } else {
        merged.push([current[0], current[1]]);
      }
    }

    return merged;
  };

  // ฟังก์ชันสำหรับบันทึก progress
  const saveProgress = useCallback(() => {
    const duration = getFiniteDuration(actualVideoLength);
    if (!progressId || watchedSeconds.length === 0 || !duration) return;

    const currentWatchedRanges = getWatchedRanges(watchedSeconds);
    const allWatchedRanges = [...safeInitialWatchedRanges, ...currentWatchedRanges];
    const finalWatchedRanges = mergeRanges(allWatchedRanges);

    // คำนวณ activeTime จาก finalWatchedRanges
    const totalWatchedSeconds = finalWatchedRanges.reduce((total, [start, end]) => {
      return total + (end - start + 1);
    }, 0);

    const activeTime = totalWatchedSeconds;
    const progress = calculatePercent(activeTime, duration);

    updateProgress({
      variables: {
        _id: progressId,
        progress: parseFloat(progress.toFixed(1)),
        activetime: activeTime,
        watchedRanges: finalWatchedRanges,
        lastWatched: getFiniteTime(currentTime),
      },
    }).catch((err) => console.error('Update progress error:', err));
  }, [progressId, watchedSeconds, safeInitialWatchedRanges, updateProgress, currentTime, actualVideoLength]);

  // บันทึก progress แบบ debounce ทุก 3 วิ
  useEffect(() => {
    if (!progressId || watchedSeconds.length === 0) return;
    const timeout = setTimeout(() => {
      saveProgress();
    }, 3000);
    return () => clearTimeout(timeout);
  }, [watchedSeconds, saveProgress, progressId]);

  const onTimeUpdate = () => {
    if (!videoRef.current) return;
    const currentVideoTime = getFiniteTime(videoRef.current.currentTime);
    const time = Math.floor(currentVideoTime);
    setCurrentTime(currentVideoTime);
    setWatchedSeconds((prev) => (prev.includes(time) ? prev : [...prev, time]));
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
      showControlsTemporarily();
    } else {
      videoRef.current.pause();
      setIsPlaying(false);

      setTimeout(() => {
        saveProgress();
      }, 100);
      showControlsTemporarily();
    }
  };

  // คลิกที่วิดีโอเพื่อเล่น/หยุด
  const handleVideoClick = () => {
    togglePlay();
  };

  // กด Spacebar เพื่อเล่น/หยุด (ยกเว้นกำลังพิมพ์ใน input/textarea/contentEditable)
  useEffect(() => {
    const onKeyDown = (e) => {
      const isSpace = e.code === 'Space' || e.key === ' ' || e.keyCode === 32;
      if (!isSpace) return;

      const t = e.target;
      const tag = (t && t.tagName) || '';
      const typing = /INPUT|TEXTAREA|SELECT/.test(tag) || (t && t.isContentEditable);
      if (typing) return;

      e.preventDefault();
      if (!videoRef.current) return;

      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
        setTimeout(() => {
          saveProgress();
        }, 100);
      }
      showControlsTemporarily();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProgressClick = (e) => {
    if (!videoRef.current) return;
    const duration = getFiniteDuration(actualVideoLength);
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    if (!width) return;
    const ratio = Math.min(1, Math.max(0, clickX / width));
    const seekTime = ratio * duration;
    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);

    setTimeout(() => {
      saveProgress();
    }, 100);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // บันทึก progress ตอนปิดหน้า หรือไปหน้าอื่น หรือ reload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const duration = getFiniteDuration(actualVideoLength);
      if (progressId && watchedSeconds.length > 0 && duration) {
        const currentWatchedRanges = getWatchedRanges(watchedSeconds);
        const allWatchedRanges = [...safeInitialWatchedRanges, ...currentWatchedRanges];
        const finalWatchedRanges = mergeRanges(allWatchedRanges);

        // คำนวณ activeTime จาก finalWatchedRanges
        const totalWatchedSeconds = finalWatchedRanges.reduce((total, [start, end]) => {
          return total + (end - start + 1);
        }, 0);

        const activeTime = totalWatchedSeconds;
        const progress = calculatePercent(activeTime, duration);

        // ใช้ navigator.sendBeacon สำหรับบันทึกข้อมูลก่อนปิดหน้า
        const data = JSON.stringify({
          _id: progressId,
          progress: parseFloat(progress.toFixed(1)),
          activetime: activeTime,
          watchedRanges: finalWatchedRanges,
          lastWatched: getFiniteTime(currentTime),
        });

        // พยายามใช้ sendBeacon ก่อน ถ้าไม่ได้ให้ใช้ fetch แบบ sync
        if (navigator.sendBeacon) {
          navigator.sendBeacon('/api/updateProgress', data);
        } else {
          // Fallback สำหรับบราวเซอร์ที่ไม่รองรับ sendBeacon
          fetch('/api/updateProgress', {
            method: 'POST',
            body: data,
            headers: {
              'Content-Type': 'application/json',
            },
            keepalive: true,
          }).catch((err) => console.error('Failed to save progress:', err));
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // หน้าเว็บถูกซ่อน (เปลี่ยนแท็บ หรือ minimize)
        saveProgress();
      }
    };

    const handlePageHide = () => {
      // หน้าเว็บกำลังจะถูกปิด หรือ reload
      const duration = getFiniteDuration(actualVideoLength);
      if (progressId && watchedSeconds.length > 0 && duration) {
        const currentWatchedRanges = getWatchedRanges(watchedSeconds);
        const allWatchedRanges = [...safeInitialWatchedRanges, ...currentWatchedRanges];
        const finalWatchedRanges = mergeRanges(allWatchedRanges);

        const totalWatchedSeconds = finalWatchedRanges.reduce((total, [start, end]) => {
          return total + (end - start + 1);
        }, 0);

        const activeTime = totalWatchedSeconds;
        const progress = calculatePercent(activeTime, duration);

        const data = JSON.stringify({
          _id: progressId,
          progress: parseFloat(progress.toFixed(1)),
          activetime: activeTime,
          watchedRanges: finalWatchedRanges,
          lastWatched: getFiniteTime(currentTime),
        });

        if (navigator.sendBeacon) {
          navigator.sendBeacon('/api/updateProgress', data);
        }
      }
    };

    // รับฟังเหตุการณ์ต่างๆ
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [progressId, watchedSeconds, currentTime, actualVideoLength, safeInitialWatchedRanges, saveProgress]);

  // เพิ่มการบันทึกเมื่อ component unmount
  useEffect(() => {
    return () => {
      // บันทึกก่อน component ถูก unmount
      if (progressId && watchedSeconds.length > 0) {
        saveProgress();
      }
    };
  }, [progressId, watchedSeconds, saveProgress]);

  // เพิ่ม event listeners สำหรับ video element
  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;

    const handlePause = () => {
      setIsPlaying(false);
      setTimeout(() => {
        saveProgress();
      }, 100);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handleSeeked = () => {
      setTimeout(() => {
        saveProgress();
      }, 100);
    };

    video.addEventListener('pause', handlePause);
    video.addEventListener('play', handlePlay);
    video.addEventListener('seeked', handleSeeked);

    return () => {
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [saveProgress]);

  // สร้าง watchedRanges สำหรับแสดงผล
  const currentWatchedRanges = getWatchedRanges(watchedSeconds);
  const allWatchedRanges = [...safeInitialWatchedRanges, ...currentWatchedRanges];
  const finalWatchedRanges = mergeRanges(allWatchedRanges);

  // คำนวณ watchedPercent จาก finalWatchedRanges
  const totalWatchedSeconds = finalWatchedRanges.reduce((total, [start, end]) => {
    return total + (end - start + 1);
  }, 0);
  const watchedPercent = calculatePercent(totalWatchedSeconds, actualVideoLength).toFixed(1);
  const currentPositionPercent = calculatePercent(currentTime, actualVideoLength);

  const formatTime = (seconds) => {
    const safeSeconds = getFiniteTime(seconds);
    const m = Math.floor(safeSeconds / 60)
      .toString()
      .padStart(2, '0');
    const s = Math.floor(safeSeconds % 60)
      .toString()
      .padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        margin: '0 auto',
        position: 'relative',
        backgroundColor: '#000',
        borderRadius: 8,
        userSelect: 'none',
        overflow: 'hidden',
      }}
    >
      <video
        ref={videoRef}
        onTimeUpdate={onTimeUpdate}
        onClick={handleVideoClick}
        style={{
          width: '100%',
          borderRadius: 8,
          display: 'block',
        }}
        controls={false}
      />

      {/* Fixed Controls Overlay */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
          padding: '20px 10px 10px',
          transform: showControls ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease-in-out',
          zIndex: 10,
        }}
      >
        {/* Progress bar */}
        <div
          onClick={handleProgressClick}
          style={{
            height: 8,
            marginBottom: 12,
            backgroundColor: 'rgba(255,255,255,0.3)',
            position: 'relative',
            borderRadius: 4,
            overflow: 'hidden',
            cursor: 'pointer',
          }}
        >
          {/* Current time indicator */}
          <div
            style={{
              position: 'absolute',
              width: `${currentPositionPercent}%`,
              height: '100%',
              backgroundColor: 'rgba(255,255,255,0.5)',
              zIndex: 1,
            }}
          />

          {/* แสดง finalWatchedRanges */}
          {finalWatchedRanges.map(([start, end], i) => (
            <div
              key={`watched-${i}`}
              style={{
                position: 'absolute',
                left: `${calculatePercent(start, actualVideoLength)}%`,
                width: `${calculatePercent(end - start + 1, actualVideoLength)}%`,
                height: '100%',
                backgroundColor: '#ff4444',
                opacity: 0.8,
                zIndex: 2,
              }}
            />
          ))}
        </div>

        {/* Controls */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            color: '#fff',
            fontSize: '14px',
          }}
        >
          <button
            onClick={togglePlay}
            style={{
              backgroundColor: 'transparent',
              color: '#fff',
              border: 0,
              padding: '8px 12px',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '16px',
            }}
            aria-label={isPlaying ? t('videoPlayer.pause') : t('videoPlayer.play')}
            title={isPlaying ? t('videoPlayer.pause') : t('videoPlayer.play')}
          >
            {isPlaying ? '⏸️' : '▶️'}
          </button>

          <span style={{ marginLeft: 12 }}>
            {formatTime(currentTime)} / {formatTime(actualVideoLength)}
          </span>

          <span style={{ marginLeft: 16 }}>{watchedPercent}%</span>

          <div style={{ flex: 1 }} />

          <span style={{ marginRight: 12, fontSize: '12px' }}>
            {t('videoPlayer.watched', { time: formatTime(totalWatchedSeconds) })}
          </span>

          <button
            onClick={toggleFullscreen}
            style={{
              backgroundColor: 'transparent',
              color: '#fff',
              border: 0,
              padding: '8px 12px',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '14px',
            }}
            aria-label={isFullscreen ? t('videoPlayer.exitFullscreen') : t('videoPlayer.fullscreen')}
            title={isFullscreen ? t('videoPlayer.exitFullscreen') : t('videoPlayer.fullscreen')}
          >
            {isFullscreen ? '⛶' : '⛶'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CloudflareVideoPlayer;
